import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PilotCodeOfConductModal from '../components/PilotCodeOfConductModal';
import './OwnerPortalPage.css';

const OwnerLoginPage = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingPilotSession, setPendingPilotSession] = useState(null);
  const { login, completeLoginAfterPilotConduct, isAuthenticated, loading: authLoading, instructor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = searchParams.get('returnUrl') || '/owner/dashboard';
  const role = (instructor?.accessRole || '').toLowerCase();

  useEffect(() => {
    if (!authLoading && isAuthenticated && !pendingPilotSession) {
      if (role && role !== 'owner') {
        navigate('/admin', { replace: true });
        return;
      }
      navigate(returnUrl.startsWith('/owner/') ? returnUrl : '/owner/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, pendingPilotSession, returnUrl, role]);

  const finishLogin = (loggedInInstructor = null) => {
    const loggedInRole = (loggedInInstructor?.accessRole || role || '').toLowerCase();
    if (loggedInRole && loggedInRole !== 'owner') {
      navigate('/admin', { replace: true });
      return;
    }
    navigate(returnUrl.startsWith('/owner/') ? returnUrl : '/owner/dashboard', { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username, password);
    if (result.success) {
      if (result.needsPilotConductAck) {
        setPendingPilotSession({ token: result.token, instructor: result.instructor });
      } else {
        finishLogin(result.instructor);
      }
    } else {
      setError(result.error || t('ownerLogin.errLogin'));
    }
    setLoading(false);
  };

  const handlePilotConductConfirm = () => {
    if (!pendingPilotSession) return;
    completeLoginAfterPilotConduct(pendingPilotSession.token, pendingPilotSession.instructor);
    setPendingPilotSession(null);
    finishLogin(pendingPilotSession.instructor);
  };

  return (
    <div className="owner-page owner-login-page">
      <section className="owner-auth-card">
        <p className="owner-eyebrow">{t('ownerLogin.eyebrow')}</p>
        <h1>{t('ownerLogin.title')}</h1>
        <p className="owner-muted">{t('ownerLogin.intro')}</p>
        {error && <div className="owner-alert owner-alert-error" role="alert">{error}</div>}
        <form onSubmit={handleSubmit} className="owner-form">
          <label>
            <span>{t('ownerLogin.emailOrUsername')}</span>
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
              disabled={loading || Boolean(pendingPilotSession)}
              autoComplete="username"
            />
          </label>
          <label>
            <span>{t('ownerLogin.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              disabled={loading || Boolean(pendingPilotSession)}
              autoComplete="current-password"
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={loading || Boolean(pendingPilotSession)}>
            {loading ? t('ownerLogin.loggingIn') : t('ownerLogin.login')}
          </button>
        </form>
        <p className="owner-login-footnote">
          <Link to="/admin/forgot-password">{t('ownerLogin.forgotPassword')}</Link>
        </p>
      </section>
      {pendingPilotSession && <PilotCodeOfConductModal onConfirm={handlePilotConductConfirm} />}
    </div>
  );
};

export default OwnerLoginPage;
